// Pure helpers for the Planner filter panel. Kept free of React/Redux so the active-summary and
// "all selected" logic can be reasoned about (and unit-tested) in isolation.

import { PROGRESS_OPTIONS } from "@/redux/planner/progressFilter/progressFilter.constants";
import { OFF_BY_DEFAULT_STATUSES } from "@/redux/planner/statusFilter/statusFilter.constants";
import { STATUSES_RESERVED } from "@/utils/constants";

// The status group's "All" baseline: every status in the list, off-by-default ones included. The
// All checkbox writes every status through setStatusFilter(""), so its full tick has to read the
// same every-status set back.
export const getStatusAllValues = (statuses = []) => statuses.map((s) => s.name);

export const isOffByDefaultStatus = (name) => OFF_BY_DEFAULT_STATUSES.includes(name);

// The status group's default selection: every status except the off-by-default ones. Initial load
// (fetchStatuses), Reset to default (resetStatuses) and a saved view with no statuses key all
// settle here, so a fresh board, a reset and a None view are identical.
export const getStatusDefaultValues = (statuses = []) =>
  statuses.map((s) => s.name).filter((name) => !isOffByDefaultStatus(name));

// Built-in statuses reach the filter as bare { uid, name } records, so their colour comes from the
// reserved table, matched by name because that table's keys are not consistently cased. Custom
// statuses carry theirs from the API under settings.
const reservedColourByName = Object.values(STATUSES_RESERVED).reduce((acc, status) => {
  acc[status.name.toLowerCase()] = status.settings?.colour ?? status.colour;
  return acc;
}, {});

export const getStatusColour = (status) =>
  status?.settings?.colour ?? reservedColourByName[status?.name?.toLowerCase()] ?? null;

// Order-independent equality for two arrays of primitive values.
export const isEqualSet = (a = [], b = []) => {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((value) => setB.has(value));
};

// Given the six group states, return the list of active (non-default) group keys.
// A group is "active" when it is narrowing the board away from its unfiltered default.
export const getActiveGroups = ({
  resources = [],
  selectedResources = [],
  users = [],
  selectedUsers = [],
  labels = [],
  selectedLabels = [],
  statuses = [],
  selectedStatuses = [],
  selectedProgress = null,
  requiredStartDate = null,
  requiredEndDate = null,
} = {}) => {
  const active = [];

  // Row visibility: default is every resource lane visible.
  if (selectedResources.length !== resources.length) {
    active.push("rowVisibility");
  }

  // Assignee: default is every user selected plus the "Unassigned" bucket. Skip while the user
  // list hasn't loaded yet (empty) so the chip doesn't flash active before fetchUsers populates
  // the "all selected + Unassigned" default.
  if (users.length > 0 && selectedUsers.length !== users.length + 1) {
    active.push("assignee");
  }

  // Labels: default is every label selected plus the "None" bucket. Skip while labels haven't
  // loaded yet, for the same reason as assignee above.
  if (labels.length > 0 && selectedLabels.length !== labels.length + 1) {
    active.push("labels");
  }

  // Workflow status: default is every status except the off-by-default ones. Ticking every status
  // is a change from that default, so it counts as active even though the chip still reads All.
  if (!isEqualSet(selectedStatuses, getStatusDefaultValues(statuses))) {
    active.push("status");
  }

  // Progress: default is all three states. `null` means "not provided" (skip). Anything other than
  // the full set of 3, including an empty selection, is narrowing the board.
  if (selectedProgress && selectedProgress.length !== PROGRESS_OPTIONS.length) {
    active.push("progress");
  }

  // Required date: default is no range.
  if (requiredStartDate || requiredEndDate) {
    active.push("requiredDate");
  }

  return active;
};

// Collapsed-row summary text: returns the full joined labels untruncated; the row applies the
// ellipsis in CSS, not here. "None" when a group is emptied (e.g. Progress).
export const selectionText = (isAllSelected, options = [], selectedValues = []) => {
  if (isAllSelected) {
    return "All";
  }
  const selected = options.filter((o) => selectedValues.includes(o.value)).map((o) => o.label);
  return selected.length > 0 ? selected.join(", ") : "None";
};

const MAX_NAMED_STATUSES = 2;

// The Workflow status row's summary. "All" for the default set and for every status. Otherwise
// it describes the smaller side of the selection, naming up to two statuses and counting past
// that; a tie reads as the excluded side. Names come out in list order.
export const statusSelectionText = (statuses = [], selectedStatuses = []) => {
  const all = getStatusAllValues(statuses);
  if (
    isEqualSet(selectedStatuses, getStatusDefaultValues(statuses)) ||
    isEqualSet(selectedStatuses, all)
  ) {
    return "All";
  }
  const selected = all.filter((name) => selectedStatuses.includes(name));
  if (selected.length === 0) {
    return "None";
  }
  const excluded = all.filter((name) => !selectedStatuses.includes(name));
  if (excluded.length <= selected.length) {
    return excluded.length <= MAX_NAMED_STATUSES
      ? `All except ${excluded.join(", ")}`
      : `All except ${excluded.length} statuses`;
  }
  return selected.length <= MAX_NAMED_STATUSES
    ? selected.join(", ")
    : `${selected.length} selected`;
};
